import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { types, flow, getRoot } from "mobx-state-tree";
import { supabase } from "@/integrations/supabase/client";
import * as buildingsRepo from "./buildings/repository";
import * as buildingDataRepo from "./buildings/buildingDataRepository";
import type { Database } from "@/integrations/supabase/types";
import type { AssignedUser } from "./buildings/repository";
import { eventLogger } from "@/utils/eventLogger";
import { BuildingLookupError } from "./buildings/lookupError";

type BuildingRow = Database["public"]["Tables"]["buildings"]["Row"];

// Minimal summary shape for list views
export const BuildingSummary = types.model("BuildingSummary", {
  id: types.identifier, // store as string for UI consistency
  name: types.string,
  address: types.string,
  org_id: types.string,
  archived: types.maybeNull(types.boolean),
  updated_at: types.string, // maps from modified_at
  year_built: types.maybeNull(types.number),
  net_area: types.maybeNull(types.number),
  registry_code: types.maybeNull(types.string),
  subscription_plan_activation_pending: types.optional(types.boolean, false),
});

export type BuildingSummaryInstance = Instance<typeof BuildingSummary>;

// Store detail as a flexible snapshot that can be hydrated progressively
// Base building row plus optional sections (kept shallow for now)
export const BuildingDetail = types.model("BuildingDetail", {
  base: types.frozen<BuildingRow>(),
  // Optional projections of child sections (thin pass)
  permits: types.maybe(types.frozen<any>()),
  technical: types.maybe(types.frozen<any>()),
  rooms: types.maybe(types.frozen<any>()),
  certifications: types.maybe(types.frozen<any>()),
  esg: types.maybe(types.frozen<any>()),
  lca: types.maybe(types.frozen<any>()),
});

export const BuildingListState = types.model("BuildingListState", {
  items: types.array(BuildingSummary),
  isLoading: types.optional(types.boolean, false),
  error: types.maybeNull(types.string),
  // Simple filters kept local to list calls
  filters: types.model({
    search: types.maybeNull(types.string),
    archived: types.maybeNull(types.boolean),
    orgId: types.maybeNull(types.string),
  }),
});

export const BuildingStore = types
  .model("BuildingStore", {
    list: types.optional(BuildingListState, {
      items: [],
      isLoading: false,
      error: null,
      filters: { search: null, archived: null, orgId: null },
    }),
    detailById: types.map(BuildingDetail),
    loadingById: types.map(types.boolean),
    errorById: types.map(types.maybeNull(types.string)),
    selectedId: types.maybe(types.string),
    lastVisitedBuildingId: types.maybe(types.string),
    lastFetchedAt: types.maybe(types.string),
    assignedUsersById: types.map(types.frozen<AssignedUser[]>()),
    loadingUsersById: types.map(types.boolean),
    usersErrorById: types.map(types.maybeNull(types.string)),
    buildingDataById: types.map(types.frozen<any>()),
    buildingDataLoadingById: types.map(types.boolean),
    buildingDataErrorById: types.map(types.maybeNull(types.string)),
  })
  .volatile(() => ({}))
  .views((self) => ({
    getById(id: string) {
      return self.detailById.get(id);
    },
    getList() {
      return self.list.items.slice();
    },
    isArchived(id: string) {
      const item = self.list.items.find((b) => b.id === id);
      return item?.archived ?? false;
    },
    roleForOrg(orgId: string) {
      const root: any = getRoot(self);
      return root?.authStore?.getRoleForOrg?.(orgId);
    },
    hasWriteAccess(arg: { building?: BuildingRow; orgId?: string }) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) return false;
      const targetOrgId = arg.orgId ?? arg.building?.org_id;
      if (!targetOrgId) return false;
      return (
        auth.hasPermission(targetOrgId, "MODIFY_DATA") ||
        auth.hasPermission(targetOrgId, "ADD_DATA")
      );
    },
    getAssignedUsers(buildingId: string) {
      return self.assignedUsersById.get(buildingId) ?? [];
    },
    isLoadingUsers(buildingId: string) {
      return self.loadingUsersById.get(buildingId) ?? false;
    },
    getUsersError(buildingId: string) {
      return self.usersErrorById.get(buildingId) ?? null;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      const saved = localStorage.getItem("lastVisitedBuildingId");
      if (saved) {
        self.lastVisitedBuildingId = saved;
      }
    },

    setLastVisitedBuildingId(id: string) {
      self.lastVisitedBuildingId = id;
      localStorage.setItem("lastVisitedBuildingId", id);
    },

    clearLastVisitedBuildingId() {
      self.lastVisitedBuildingId = undefined;
      localStorage.removeItem("lastVisitedBuildingId");
    },

    clear() {
      self.list.items.clear();
      self.list.isLoading = false;
      self.list.error = null;
      self.detailById.clear();
      self.loadingById.clear();
      self.errorById.clear();
      self.selectedId = undefined;
      self.lastVisitedBuildingId = undefined;
      localStorage.removeItem("lastVisitedBuildingId");
      self.lastFetchedAt = undefined;
    },

    listBuildings: flow(function* listBuildings(
      orgId?: string,
      opts?: { search?: string; archived?: boolean; limit?: number }
    ) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) {
        self.list.error = "Authentication required";
        throw new Error("Authentication required");
      }

      // Organization is optional; RLS will scope results if not provided
      const effectiveOrgId: string | null =
        (orgId as string | undefined) ?? localStorage.getItem("currentOrganizationId") ?? null;

      self.list.isLoading = true;
      self.list.error = null;
      self.list.filters.orgId = effectiveOrgId;
      self.list.filters.search = opts?.search ?? null;
      self.list.filters.archived = opts?.archived ?? null;

      try {
        const data: any[] = yield buildingsRepo.list({
          orgId: effectiveOrgId ?? undefined,
          search: opts?.search,
          archived: typeof opts?.archived === "boolean" ? opts.archived : null,
          limit: opts?.limit ?? 100,
        });

        const items = (data ?? []).map((r: any) =>
          BuildingSummary.create({
            id: String(r.id),
            name: r.name,
            address: r.address,
            org_id: r.org_id,
            archived: r.archived ?? false,
            updated_at: r.modified_at,
            year_built: r.year_built ?? null,
            net_area: r.net_area ?? null,
            registry_code: r.registry_code ?? null,
            subscription_plan_activation_pending: r.subscription_plan_activation_pending ?? false,
          })
        );
        self.list.items.replace(items);
        self.lastFetchedAt = new Date().toISOString();
      } catch (e: any) {
        console.error("listBuildings error", e);
        self.list.error = e?.message ?? String(e);
        throw e;
      } finally {
        self.list.isLoading = false;
      }
    }),

    countBuildings: flow(function* countBuildings(orgId?: string) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) throw new Error("Authentication required");
      const effectiveOrgId: string | null =
        (orgId as string | undefined) ?? localStorage.getItem("currentOrganizationId") ?? null;
      const total: number = yield buildingsRepo.count({ orgId: effectiveOrgId ?? undefined });
      return total;
    }),

    // Non-mutating building search that bypasses list state updates
    searchBuildings: flow(function* searchBuildings(params: {
      orgId?: string;
      query: string;
      limit?: number;
      archived?: boolean | null;
    }): Generator<Promise<unknown>, BuildingSummaryInstance[], unknown> {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) throw new Error("Authentication required");

      type BuildingListItem = Pick<
        BuildingRow,
        | "id"
        | "name"
        | "address"
        | "org_id"
        | "archived"
        | "modified_at"
        | "year_built"
        | "net_area"
        | "registry_code"
        | "subscription_plan_activation_pending"
      >;

      const data = (yield buildingsRepo.list({
        orgId: params.orgId,
        search: params.query,
        archived: typeof params.archived === "boolean" ? params.archived : null,
        limit: params.limit ?? 5,
      })) as BuildingListItem[];

      return (data ?? []).map((r) =>
        BuildingSummary.create({
          id: String(r.id),
          name: r.name,
          address: r.address,
          org_id: r.org_id,
          archived: r.archived ?? false,
          updated_at: r.modified_at,
          year_built: r.year_built ?? null,
          net_area: r.net_area ?? null,
          registry_code: r.registry_code ?? null,
          subscription_plan_activation_pending: r.subscription_plan_activation_pending ?? false,
        })
      );
    }),

    loadDetail: flow(function* loadDetail(buildingId: string) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) {
        const error = new BuildingLookupError("unauthenticated");
        self.errorById.set(buildingId, error.message);
        throw error;
      }

      self.loadingById.set(buildingId, true);
      self.errorById.set(buildingId, null);

      try {
        const building = yield buildingsRepo.getById(buildingId);
        if (!building) throw new BuildingLookupError("not_found");

        // Permission: admin of org OR created_by == userProfile.id
        const userStore = root?.userStore;
        const userProfileId: number | undefined = userStore?.userProfile?.id;
        if (!userProfileId) throw new Error("User profile missing");

        // Permission check: user must belong to the org via ANY membership path —
        // org_user_roles (classic), user_building_assignments (building-scoped),
        // or user_project_assignments (project-scoped, e.g. subcontractor).
        let hasOrgAccess = false;
        try {
          const [rolesRes, buildingRes, projectRes] = yield Promise.all([
            supabase
              .from("org_user_roles")
              .select("user_id")
              .eq("user_id", userProfileId)
              .eq("org_id", building.org_id),
            supabase
              .from("user_building_assignments")
              .select("user_id")
              .eq("user_id", userProfileId)
              .eq("org_id", building.org_id),
            supabase
              .from("user_project_assignments")
              .select("user_id")
              .eq("user_id", userProfileId)
              .eq("org_id", building.org_id),
          ]);
          if (rolesRes.error) throw rolesRes.error;
          if (buildingRes.error) throw buildingRes.error;
          if (projectRes.error) throw projectRes.error;
          hasOrgAccess =
            (rolesRes.data && rolesRes.data.length > 0) ||
            (buildingRes.data && buildingRes.data.length > 0) ||
            (projectRes.data && projectRes.data.length > 0);
        } catch (roleCheckErr) {
          // Fallback to authStore if one of the queries failed
          const role = auth.getRoleForOrg?.(building.org_id);
          hasOrgAccess = !!role || auth.memberOrgIds?.has?.(building.org_id);
        }

        if (!hasOrgAccess) {
          throw new BuildingLookupError("forbidden");
        }

        // Base detail
        const detail = BuildingDetail.create({ base: building });

        // Section-level loading (previously supported "permits") removed with dead tables cleanup.

        self.detailById.set(String(building.id), detail);
        return detail;
      } catch (e: any) {
        console.error("loadDetail error", e);
        self.errorById.set(buildingId, e?.message ?? String(e));
        throw e;
      } finally {
        self.loadingById.set(buildingId, false);
      }
    }),

    createBuilding: flow(function* createBuilding(input: {
      name: string;
      address: string;
      cadastral_code: string;
      year_built?: number;
      net_area?: number | null;
      registry_code?: string | null;
      orgId?: string;
    }) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      const userStore = root?.userStore;
      if (!auth?.isAuthenticated) throw new Error("Authentication required");
      const userProfileId: number | undefined = userStore?.userProfile?.id;
      if (!userProfileId) throw new Error("User profile missing");

      const orgId = input.orgId || localStorage.getItem("currentOrganizationId") || undefined;
      if (!orgId) throw new Error("Organisation required");

      const inserted: any = yield buildingsRepo.create({
        name: input.name,
        address: input.address,
        cadastral_code: input.cadastral_code,
        year_built: input.year_built,
        net_area: input.net_area ?? null,
        registry_code: input.registry_code ?? null,
        created_by: userProfileId,
        org_id: orgId,
      });

      // Update list cache
      const summary = BuildingSummary.create({
        id: String(inserted.id),
        name: inserted.name,
        address: inserted.address,
        org_id: inserted.org_id,
        archived: inserted.archived ?? false,
        updated_at: inserted.modified_at,
        year_built: input.year_built ?? new Date().getFullYear(),
        net_area: input.net_area ?? null,
        registry_code: inserted.registry_code ?? null,
        subscription_plan_activation_pending:
          inserted.subscription_plan_activation_pending ?? false,
      });
      self.list.items.unshift(summary);
      return summary;
    }),

    initializeBuildingData: flow(function* initializeBuildingData(buildingId: number) {
      try {
        yield buildingsRepo.initializeBuildingData(buildingId);
      } catch (e: any) {
        console.error("initializeBuildingData error", e);
        throw e;
      }
    }),

    updateBuildingName: flow(function* updateBuildingName(buildingId: string, name: string) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) throw new Error("Authentication required");

      // Fetch to check permissions
      const building = yield buildingsRepo.getById(buildingId);
      if (!building) throw new Error("Building not found");

      if (!self.hasWriteAccess({ building })) {
        throw new Error("Write access required");
      }

      const data: any = yield buildingsRepo.updateName(buildingId, name);

      // Refresh caches
      const idx = self.list.items.findIndex((b) => b.id === String(building.id));
      if (idx >= 0) {
        const prev = self.list.items[idx];
        self.list.items[idx] = BuildingSummary.create({
          id: String(data.id),
          name: data.name,
          address: data.address,
          org_id: data.org_id,
          archived: data.archived ?? false,
          updated_at: data.modified_at,
          year_built: prev.year_built ?? null,
          net_area: prev.net_area ?? null,
          registry_code: prev.registry_code ?? null,
          subscription_plan_activation_pending: prev.subscription_plan_activation_pending,
        });
      }
      if (self.detailById.has(String(building.id))) {
        const detail = self.detailById.get(String(building.id))!;
        self.detailById.set(
          String(building.id),
          BuildingDetail.create({
            ...detail,
            base: { ...detail.base, name: data.name, modified_at: data.modified_at } as BuildingRow,
          })
        );
      }
    }),

    updateBuildingFields: flow(function* updateBuildingFields(
      buildingId: string,
      patch: Partial<BuildingRow>
    ) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) throw new Error("Authentication required");

      // Fetch to check permissions
      const building = yield buildingsRepo.getById(buildingId);
      if (!building) throw new Error("Building not found");

      if (!self.hasWriteAccess({ building })) {
        throw new Error("Write access required");
      }

      const data: any = yield buildingsRepo.updateFields(buildingId, patch as any);

      // Refresh list cache
      const idx = self.list.items.findIndex((b) => b.id === String(building.id));
      if (idx >= 0) {
        const prev = self.list.items[idx];
        self.list.items[idx] = BuildingSummary.create({
          id: String(data.id),
          name: data.name,
          address: data.address,
          org_id: data.org_id,
          archived: data.archived ?? false,
          updated_at: data.modified_at,
          year_built: prev.year_built ?? null,
          net_area: prev.net_area ?? null,
          registry_code: prev.registry_code ?? null,
          subscription_plan_activation_pending: prev.subscription_plan_activation_pending,
        });
      }
      // Refresh detail cache if present
      if (self.detailById.has(String(building.id))) {
        const detail = self.detailById.get(String(building.id))!;
        self.detailById.set(
          String(building.id),
          BuildingDetail.create({
            ...detail,
            base: { ...detail.base, ...patch, modified_at: data.modified_at } as BuildingRow,
          })
        );
      }
      return data;
    }),

    archiveBuilding: flow(function* archiveBuilding(buildingId: string, archived: boolean) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      const userStore = root?.userStore;
      if (!auth?.isAuthenticated) throw new Error("Authentication required");
      const userProfileId: number | undefined = userStore?.userProfile?.id;
      if (!userProfileId) throw new Error("User profile missing");

      // Fetch building to check org and permissions
      const building = yield buildingsRepo.getById(buildingId);
      if (!building) throw new Error("Building not found");
      const orgId = building.org_id as string;
      if (!auth.hasPermission(orgId, "MODIFY_DATA")) {
        throw new Error("Modify permission required");
      }

      const data: any = yield buildingsRepo.setArchived(buildingId, archived, userProfileId);

      // Update list cache
      const idx = self.list.items.findIndex((b) => b.id === String(data.id));
      if (idx >= 0) {
        const prev = self.list.items[idx];
        self.list.items[idx] = BuildingSummary.create({
          id: String(data.id),
          name: data.name,
          address: data.address,
          org_id: data.org_id,
          archived: data.archived ?? false,
          updated_at: data.modified_at,
          registry_code: prev?.registry_code ?? null,
          subscription_plan_activation_pending: prev?.subscription_plan_activation_pending ?? false,
        });
      }
      // Update detail cache if present
      if (self.detailById.has(String(data.id))) {
        const detail = self.detailById.get(String(data.id))!;
        self.detailById.set(
          String(data.id),
          BuildingDetail.create({
            ...detail,
            base: {
              ...detail.base,
              archived: data.archived,
              modified_at: data.modified_at,
            } as BuildingRow,
          })
        );
      }
    }),

    loadAssignedUsers: flow(function* loadAssignedUsers(buildingId: string, orgId: string) {
      const root: any = getRoot(self);
      const auth = root?.authStore;
      if (!auth?.isAuthenticated) {
        self.usersErrorById.set(buildingId, "Authentication required");
        throw new Error("Authentication required");
      }

      self.loadingUsersById.set(buildingId, true);
      self.usersErrorById.set(buildingId, null);

      try {
        const users: AssignedUser[] = yield buildingsRepo.assignedUsers.list(buildingId, orgId);
        self.assignedUsersById.set(buildingId, users);
        return users;
      } catch (e: any) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        self.usersErrorById.set(buildingId, errorMsg);
        throw e;
      } finally {
        self.loadingUsersById.set(buildingId, false);
      }
    }),

    clearAssignedUsers(buildingId: string) {
      self.assignedUsersById.delete(buildingId);
      self.loadingUsersById.delete(buildingId);
      self.usersErrorById.delete(buildingId);
    },

    loadBuildingData: flow(function* loadBuildingData(buildingId: string) {
      self.buildingDataLoadingById.set(buildingId, true);
      self.buildingDataErrorById.set(buildingId, null);

      try {
        // Fetch both building_data rows and original building record in parallel
        const [rows, originalBuilding] = yield Promise.all([
          buildingDataRepo.fetchByBuildingId(buildingId),
          buildingsRepo.getById(buildingId),
        ]);
        const structured = transformBuildingData(
          rows,
          originalBuilding?.address,
          originalBuilding?.registry_code
        );
        self.buildingDataById.set(buildingId, structured);
        return structured;
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        self.buildingDataErrorById.set(buildingId, errorMsg);
        throw e;
      } finally {
        self.buildingDataLoadingById.set(buildingId, false);
      }
    }),
  }))
  // These actions call loadBuildingData, which is available on the typed self
  // after the preceding actions block has been applied.
  .actions((self) => ({
    updateBuildingDataField: flow(function* updateBuildingDataField(
      buildingId: string,
      orgId: string,
      attributeType: string,
      attributeName: string,
      valueType: "text" | "number" | "date",
      value: string | null
    ) {
      const root: any = getRoot(self);
      const userStore = root?.userStore;
      const userId = userStore?.userProfile?.id;

      if (!userId) throw new Error("User profile missing");

      // Capture previous value for event logging
      const existingData = self.buildingDataById.get(buildingId);
      const previousValue =
        existingData?.[attributeType as keyof typeof existingData]?.[
          attributeName as keyof (typeof existingData)[keyof typeof existingData]
        ];

      try {
        const updated = yield buildingDataRepo.upsertUserValue(
          buildingId,
          orgId,
          attributeType,
          attributeName,
          valueType,
          value,
          userId
        );

        yield self.loadBuildingData(buildingId);

        // Log event (fire-and-forget) - centralized here in the store
        const eventType =
          attributeType === "technical"
            ? "building.technical_data.updated"
            : "building.info.updated";
        eventLogger.log({
          event_type: eventType,
          entity_type: "building",
          entity_id: buildingId,
          org_id: orgId,
          field_name: attributeName,
          previous_value: previousValue,
          new_value: value,
          metadata: { attributeType },
        });

        return updated;
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        throw new Error(errorMsg);
      }
    }),

    /**
     * Update the building's original address in the buildings table.
     * This is separate from building_data updates as the address set at creation
     * should be the single source of truth (WHA-510).
     */
    updateAddress: flow(function* updateAddress(buildingId: string, address: string) {
      try {
        yield buildingsRepo.updateFields(buildingId, { address });
        // Reload building data to refresh the originalAddress
        yield self.loadBuildingData(buildingId);
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        throw new Error(errorMsg);
      }
    }),

    /**
     * Update the building's registry code in the buildings table and reset
     * state to UNPROCESSED so the EHR pipeline re-runs (WHA-1372).
     */
    updateRegistryCode: flow(function* updateRegistryCode(
      buildingId: string,
      registryCode: string | null
    ) {
      try {
        yield buildingsRepo.updateFields(buildingId, {
          registry_code: registryCode,
          state: "UNPROCESSED",
        });
        yield self.loadBuildingData(buildingId);
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        throw new Error(errorMsg);
      }
    }),

    /**
     * Save user-entered building attributes to data.user_data and sync
     * registry_code / net_area back to the buildings table (WHA-2868 / WHA-2869).
     */
    saveUserAttributes: flow(function* saveUserAttributes(
      buildingId: string,
      orgId: string,
      fields: Record<string, string>
    ) {
      const root: any = getRoot(self);
      const userStore = root?.userStore;
      const userProfileId: number | undefined = userStore?.userProfile?.id;
      if (!userProfileId) throw new Error("User profile missing");

      const buildingIdNum = parseInt(buildingId, 10);

      try {
        // Insert each changed field as a new audit row in data.user_data
        for (const [key, value] of Object.entries(fields)) {
          if (value !== undefined && value !== null) {
            yield buildingsRepo.saveUserData({
              orgId,
              buildingId: buildingIdNum,
              attributeKey: key,
              attributeValue: value,
              editedBy: userProfileId,
            });
          }
        }

        // Sync fields that also live on public.buildings
        if (fields.registry_code !== undefined) {
          yield buildingsRepo.updateFields(buildingId, {
            registry_code: fields.registry_code || null,
            state: "UNPROCESSED",
          });
        }
        if (fields.net_area !== undefined) {
          const numericArea = fields.net_area ? parseFloat(fields.net_area) : null;
          yield buildingsRepo.updateFields(buildingId, {
            net_area: numericArea,
          });
        }

        // Reload building data to refresh caches
        yield self.loadBuildingData(buildingId);
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        throw new Error(errorMsg);
      }
    }),

    markBuildingDataAsReviewed: flow(function* markBuildingDataAsReviewed(
      buildingId: string,
      attributeType: string,
      attributeName: string
    ) {
      const root: any = getRoot(self);
      const userStore = root?.userStore;
      const userId = userStore?.userProfile?.id;

      if (!userId) throw new Error("User profile missing");

      try {
        yield buildingDataRepo.markAsReviewed(buildingId, attributeType, attributeName, userId);
        yield self.loadBuildingData(buildingId);
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        throw new Error(errorMsg);
      }
    }),

    markMultipleBuildingDataAsReviewed: flow(function* markMultipleBuildingDataAsReviewed(
      buildingId: string,
      attributes: Array<{ attributeType: string; attributeName: string }>
    ) {
      const root: any = getRoot(self);
      const userStore = root?.userStore;
      const userId = userStore?.userProfile?.id;

      if (!userId) throw new Error("User profile missing");

      try {
        yield buildingDataRepo.markMultipleAsReviewed(buildingId, attributes, userId);
        yield self.loadBuildingData(buildingId);
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        throw new Error(errorMsg);
      }
    }),

    markAllBuildingDataAsReviewed: flow(function* markAllBuildingDataAsReviewed(
      buildingId: string
    ) {
      const root: any = getRoot(self);
      const userStore = root?.userStore;
      const userId = userStore?.userProfile?.id;

      if (!userId) throw new Error("User profile missing");

      try {
        yield buildingDataRepo.markAllAsReviewed(buildingId, userId);
        yield self.loadBuildingData(buildingId);
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        throw new Error(errorMsg);
      }
    }),

    getUnreviewedBuildingDataCount: flow(function* getUnreviewedBuildingDataCount(
      buildingId: string
    ) {
      try {
        const count: number = yield buildingDataRepo.getUnreviewedCount(buildingId);
        return count;
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : String(e);
        throw new Error(errorMsg);
      }
    }),
  }));

function transformBuildingData(
  rows: any[],
  originalAddress?: string | null,
  registryCode?: string | null
) {
  const result: any = {
    building: {},
    technical: {},
    utilities: {},
    certifications: {},
    metadata: {},
  };

  if (rows.length > 0) {
    result.building.org_id = rows[0].org_id;
  }

  // Store the original address from buildings table (set at creation)
  // This is used instead of building_data.address to ensure consistency
  if (originalAddress !== undefined) {
    result.building.originalAddress = originalAddress;
  }

  // Store registry_code from buildings table for display (WHA-1372)
  if (registryCode !== undefined) {
    result.building.registryCodeFromBuildings = registryCode;
  }

  rows.forEach((row) => {
    const fieldKey = row.attribute_name;

    // Store the full row data for each field
    const rowData = {
      value_txt: row.value_txt,
      user_value: row.user_value,
      attribute_json: row.attribute_json,
      reviewed: row.reviewed ?? false,
      modified_at: row.modified_at,
    };

    if (row.attribute_type === "general") {
      result.building[fieldKey] = rowData;
    } else if (row.attribute_type === "technical") {
      result.technical[fieldKey] = rowData;
    } else if (row.attribute_type === "utilities") {
      result.utilities[fieldKey] = rowData;
    } else if (row.attribute_type === "certifications") {
      result.certifications[fieldKey] = rowData;
    }
  });

  return result;
}

export type BuildingStoreInstance = Instance<typeof BuildingStore>;
export type BuildingStoreSnapshotIn = SnapshotIn<typeof BuildingStore>;
export type BuildingStoreSnapshotOut = SnapshotOut<typeof BuildingStore>;
